import React, { useState, ChangeEvent, useEffect } from "react";
import {
  DataGrid,
  GridColDef,
  GridRowsProp,
  GridRenderCellParams
} from "@mui/x-data-grid";
import {
  Drawer,
  TextField,
  Button,
  Container,
  Box,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  ListItemText,
  InputLabel,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActionArea,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { SERVER_BASE_URL } from "../Config";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatStatus, formatDeliveryMethod, IN_PROGRESS_STATUSES, AVAILABLE_STATUSES } from "../utils/statusUtils";
import AdminUpload from "./AdminUpload";
import CustomPricingUpload from "./CustomPricingUpload";
import ProductManagement from "./ProductManagement";
import ProviderManagement from "./ProviderManagement";
import ProviderRequests from "./ProviderRequests";
import PharmacistManagement from "./PharmacistManagement";
import SalesRepManagement from "./SalesRepManagement";

const formatDate = (dateString: string | null) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/New_York"
  });
};

const formatDateShort = (dateString: string | null) => {
  if (!dateString) return "";
  // Append T12:00:00 to date-only strings to avoid timezone shifting the date back a day
  const normalized = dateString.includes('T') ? dateString : dateString + 'T12:00:00';
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};


type RowData = {
  id: number;
  prescribedAt: string;
  needByDate: string;
  isAsap: boolean;
  patientName: string;
  patientDob: string;
  doctorName: string;
  providerName: string;
  status: string;
  drugName: string;
  strength: string;
  duration: string;
  quantity: number;
  lastNote: string;
  lastUpdated: string;
  deliveryMethod: string;
};

interface PrescriptionsProps {
  showAdmin?: boolean;
  permissionLevel?: string;
}

const Prescriptions: React.FC<PrescriptionsProps> = ({ showAdmin = false, permissionLevel = 'full_access' }) => {
  const [rows, setRows] = useState<GridRowsProp<RowData>>([]);
  const [filteredRows, setFilteredRows] = useState<GridRowsProp<RowData>>([]);
  const [selectedRow, setSelectedRow] = useState<RowData | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(() => localStorage.getItem("pharmacySearchQuery") || "");
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    const saved = localStorage.getItem("pharmacyStatusFilter");
    return saved ? JSON.parse(saved) : [...IN_PROGRESS_STATUSES];
  });
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const showProductManagement = searchParams.get("section") === "products";
  const showProviderManagement = searchParams.get("section") === "providers";
  const showProviderRequests = searchParams.get("section") === "provider-requests";
  const showPharmacistManagement = searchParams.get("section") === "pharmacists";
  const showSalesReps = searchParams.get("section") === "sales-reps";

  const loadOrders = async () => {
    try {
      setLoading(true);
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const params = new URLSearchParams();
      if (startDate) params.set("start_date", startDate.format("YYYY-MM-DD"));
      if (endDate) params.set("end_date", endDate.format("YYYY-MM-DD"));
      if (statusFilter.length > 0) params.set("status", statusFilter.join(","));
      const response = await fetch(`${SERVER_BASE_URL}/manage_orders?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${pharmacistToken}`
        }
      });

      if (!response.ok) return;
      const data = await response.json();
      const orders = data["orders"];
      if (!orders) return;
      const clientOrders = orders.map((order: any) => ({
        id: order.id,
        prescribedAt: order.created,
        needByDate: order.needBy,
        isAsap: order.isAsap,
        patientName: order.patient.name,
        patientDob: order.patient.dob,
        doctorName: order.doctor ? order.doctor.name : "—",
        providerName: order.provider.name,
        status: order.status,
        drugName: order.products[0].product.name,
        strength: order.products[0].strength,
        duration: order.products[0].duration,
        quantity: order.products[0].quantity,
        lastNote: order.updates.length ? order.updates[order.updates.length - 1].note : "",
        lastUpdated: order.updates.length ? order.updates[order.updates.length - 1].created : "",
        deliveryMethod: order.deliveryMethod || "Unknown"
      }));
      setRows(clientOrders);
      applyFilters(searchQuery, statusFilter, clientOrders);
    } catch (err) {
      console.error("Failed to load orders:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [startDate, endDate, statusFilter]);


  const handleEditClick = (row: RowData) => {
    setSelectedRow(row);
    setIsDrawerOpen(true);
  };

  const navigate = useNavigate();

  const handleDoubleClick = (row: RowData) => {
    navigate(`/manage/${row.id}`);
  };

  const handleCloseDrawer = () => {
    setSelectedRow(null);
    setIsDrawerOpen(false);
  };

  const handleSave = () => {
    if (selectedRow) {
      setRows((prevRows) =>
        prevRows.map((row) => (row.id === selectedRow.id ? selectedRow : row))
      );
      setIsDrawerOpen(false);
      applyFilters();
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    localStorage.setItem("pharmacySearchQuery", query);
    applyFilters(query, statusFilter);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    localStorage.setItem("pharmacySearchQuery", "");
    applyFilters("", statusFilter);
  };

  const handleStatusFilterChange = (event: any) => {
    const value = event.target.value as string[];
    setStatusFilter(value);
    localStorage.setItem("pharmacyStatusFilter", JSON.stringify(value));
    applyFilters(searchQuery, value);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    localStorage.setItem("pharmacySearchQuery", "");
    applyFilters("", statusFilter);
  };

  const applyFilters = (query = searchQuery, _statuses = statusFilter, rowsData = rows) => {
    let filtered = rowsData;
    if (query) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter((row) =>
        Object.values(row).some((value) =>
          value.toString().toLowerCase().includes(lowerQuery)
        )
      );
    }
    filtered = [...filtered].sort((a, b) => new Date(b.prescribedAt).getTime() - new Date(a.prescribedAt).getTime());
    setFilteredRows(filtered);
  };

  const handleExport = async () => {
    const pharmacistToken = localStorage.getItem("pharmacistToken");
    const params = new URLSearchParams();
    if (searchQuery) params.set("search", searchQuery);
    if (statusFilter.length > 0) params.set("status", statusFilter.join(","));
    if (startDate) params.set("start_date", startDate.format("YYYY-MM-DD"));
    if (endDate) params.set("end_date", endDate.format("YYYY-MM-DD"));
    const url = `${SERVER_BASE_URL}/export_orders?${params.toString()}`;
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${pharmacistToken}` },
      });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      const disposition = response.headers.get("Content-Disposition");
      a.download = disposition?.match(/filename="(.+)"/)?.[1] || "orders_export.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  const showInProgressOrders = () => {
    setStatusFilter([...IN_PROGRESS_STATUSES]);
    localStorage.setItem("pharmacyStatusFilter", JSON.stringify([...IN_PROGRESS_STATUSES]));
    applyFilters(searchQuery, [...IN_PROGRESS_STATUSES]);
  };

  const renderStatusChip = (status: string) => {
    const label = formatStatus(status);
    const chipStyles: Record<string, { backgroundColor: string; color: string }> = {
      PENDING: { backgroundColor: "#ed6c02", color: "#fff" },
      DELIVERED: { backgroundColor: "#2e7d32", color: "#fff" },
      PROCESSING_EXCEPTION: { backgroundColor: "#1976d2", color: "#fff" },
      CANCELLED: { backgroundColor: "#d32f2f", color: "#fff" },
      CANCELLATION_REQUESTED: { backgroundColor: "#fff", color: "#d32f2f" },
    };
    const style = chipStyles[status] || {};
    return <Chip label={label} size="small" sx={{ ...style, border: status === "CANCELLATION_REQUESTED" ? "1px solid #d32f2f" : undefined }} />;
  };

  const columns: GridColDef<RowData>[] = [
    {
      field: "prescribedAt",
      headerName: "Prescribed At",
      flex: 1,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: "needByDate",
      headerName: "Need By",
      flex: 1,
      renderCell: (params) => {
        const row = params.row as RowData;
        return row.isAsap ? (
          <Chip label="ASAP" size="small" color="error" />
        ) : (
          formatDateShort(params.value)
        );
      }
    },
    {
      field: "id",
      headerName: "Order #",
      flex: 0.5,
      renderCell: (params) => `#${params.value}`
    },
    { field: "patientName", headerName: "Patient", flex: 1 },
    { field: "providerName", headerName: "Provider", flex: 1 },
    { field: "drugName", headerName: "Drug", flex: 1 },
    { field: "deliveryMethod", headerName: "Delivery", flex: 1, renderCell: (params) =>
      params.value === "shipToPatient"
        ? <Chip label="Ship to Patient" size="small" sx={{ backgroundColor: '#ffeb3b', color: '#000' }} />
        : formatDeliveryMethod(params.value)
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => renderStatusChip(params.value)
    },
    {
      field: "lastUpdated",
      headerName: "Last Updated",
      flex: 1,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: "lastNote",
      headerName: "Last Note",
      flex: 2,
    },
    {
      field: "Manage",
      headerName: "Edit",
      sortable: false,
      renderCell: (params: GridRenderCellParams<RowData>) => (
        <IconButton href={`/manage/${params.row.id}`}>
          <ManageAccountsIcon />
        </IconButton>
      ),
      flex: 0.5
    }
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Container maxWidth={false}>
      {showAdmin ? (
        <Box my={2}>
          <Typography sx={{ typography: { xs: "h5", sm: "h4" }, mt: { xs: 1, md: 4 } }} mb={3}>Pharmacy Administration</Typography>
          {showProductManagement ? (
            <ProductManagement onBack={() => setSearchParams({ admin: "true" })} />
          ) : showProviderManagement ? (
            <Box>
              <Button
                variant="outlined"
                onClick={() => setSearchParams({ admin: "true" })}
                sx={{ mb: 2 }}
              >
                ← Back to Admin Tools
              </Button>
              <ProviderManagement />
            </Box>
          ) : showProviderRequests ? (
            <Box>
              <Button
                variant="outlined"
                onClick={() => setSearchParams({ admin: "true" })}
                sx={{ mb: 2 }}
              >
                ← Back to Admin Tools
              </Button>
              <ProviderRequests onBack={() => setSearchParams({ admin: "true" })} />
            </Box>
          ) : showPharmacistManagement ? (
            <Box>
              <Button
                variant="outlined"
                onClick={() => setSearchParams({ admin: "true" })}
                sx={{ mb: 2 }}
              >
                ← Back to Admin Tools
              </Button>
              <PharmacistManagement />
            </Box>
          ) : showSalesReps ? (
            <Box>
              <Button
                variant="outlined"
                onClick={() => setSearchParams({ admin: "true" })}
                sx={{ mb: 2 }}
              >
                ← Back to Admin Tools
              </Button>
              <SalesRepManagement />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, maxWidth: 900, mx: { xs: 0, sm: "auto" }, alignItems: { md: "stretch" }, mt: { xs: 0, md: "15vh" } }}>
              {/* Product Database + Custom Pricing Section */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
                <AdminUpload />
                <CustomPricingUpload />
              </Box>

              {/* Admin Tools Section */}
              <Box sx={{ flex: 1, display: "flex" }}>
              <Paper sx={{ p: 3, flex: 1 }}>
                <Typography variant="h5" mb={3}>
                  Admin Tools
                </Typography>
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    onClick={() => setSearchParams({ admin: "true", section: "products" })}
                    fullWidth
                    size="large"
                  >
                    Manage Products
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setSearchParams({ admin: "true", section: "providers" })}
                    fullWidth
                    size="large"
                    color="secondary"
                  >
                    Manage Provider Accounts
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setSearchParams({ admin: "true", section: "provider-requests" })}
                    fullWidth
                    size="large"
                    color="info"
                  >
                    Provider Account Requests
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setSearchParams({ admin: "true", section: "sales-reps" })}
                    fullWidth
                    size="large"
                    color="success"
                  >
                    Manage Sales Reps
                  </Button>
                  {permissionLevel === 'full_access' && (
                    <Button
                      variant="contained"
                      onClick={() => setSearchParams({ admin: "true", section: "pharmacists" })}
                      fullWidth
                      size="large"
                      color="warning"
                    >
                      Manage Admin Accounts
                    </Button>
                  )}
                </Stack>
              </Paper>
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        <>
          <Box my={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
              <TextField
                value={searchQuery}
                onChange={handleSearchChange}
                label="Search orders..."
                variant="outlined"
                sx={{ flexGrow: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearSearch}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <FormControl sx={{ minWidth: { xs: 0, sm: 200 } }}>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="Status"
                  renderValue={(selected) => {
                    const count = (selected as string[]).length;
                    return count === 0 ? "All Statuses" : `${count} status${count === 1 ? '' : 'es'} selected`;
                  }}
                >
                  {AVAILABLE_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={statusFilter.indexOf(status) > -1} />
                      <ListItemText primary={formatStatus(status)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  maxDate={dayjs()}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: { sx: { minWidth: { xs: 0, sm: 150 } } },
                    field: { clearable: true },
                    actionBar: { actions: ['today', 'clear'] },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  maxDate={dayjs()}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: { sx: { minWidth: { xs: 0, sm: 150 } } },
                    field: { clearable: true },
                    actionBar: { actions: ['today', 'clear'] },
                  }}
                />
              </LocalizationProvider>

              <Button variant="contained" onClick={showInProgressOrders}>
                In Progress Only
              </Button>
              <Button variant="outlined" onClick={handleExport} startIcon={<FileDownloadIcon />}>
                Export
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
              {searchQuery && (
                <Chip label={searchQuery} onDelete={handleClearSearch} sx={{ backgroundColor: "#d32f2f", color: "#fff", "& .MuiChip-deleteIcon": { color: "#fff" } }} />
              )}
            </Stack>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {filteredRows.map((row) => (
                <Card key={row.id} variant="outlined">
                  <CardActionArea onClick={() => handleDoubleClick(row)}>
                    <CardContent sx={{ textAlign: "left" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          #{row.id} · {row.patientName}
                        </Typography>
                        {renderStatusChip(row.status)}
                      </Box>
                      <Typography variant="body2" color="text.secondary" component="div">
                        Need By: {row.isAsap ? <Chip label="ASAP" size="small" color="error" /> : (formatDateShort(row.needByDate) || "—")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {row.drugName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Provider: {row.providerName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Prescribed: {formatDateShort(row.prescribedAt)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Delivery: {row.deliveryMethod === "shipToPatient"
                          ? <Chip label="Ship to Patient" size="small" sx={{ backgroundColor: '#ffeb3b', color: '#000' }} />
                          : formatDeliveryMethod(row.deliveryMethod)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
              {filteredRows.length === 0 && (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                  No orders found.
                </Typography>
              )}
            </Box>
          ) : (
            <Box height={400}>
              <DataGrid rows={filteredRows} columns={columns} autoHeight
                        initialState={{
                          sorting: {
                            sortModel: [{ field: "prescribedAt", sort: "desc" }]
                          }
                        }}
                        onRowDoubleClick={(params) => handleDoubleClick(params.row)} />
            </Box>
          )}
        </>
      )}

      <Drawer anchor="right" open={isDrawerOpen} onClose={handleCloseDrawer} sx={{ width: 300 }}>
        {selectedRow && (
          <Box sx={{ padding: 2, width: 300 }}>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <h2>Edit Details</h2>
              <IconButton onClick={handleCloseDrawer}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Button onClick={handleSave} variant="contained" color="primary" fullWidth>
              Save
            </Button>
          </Box>
        )}
      </Drawer>
    </Container>
  );
};

export default Prescriptions;
